import API from '@/app/api/internalAPIs';
import {getCurrentLocaleForDateUtils} from '@/app/utils/helper';
import Service from '@/app/utils/service';
import translate from '@/app/utils/translate';

import {
  AllowanceProposalItem,
  AllowanceProposalItemFormFormat,
  BenefitProposalItem,
  BenefitProposalItemFormFormat,
  EmployeeBasicData,
  FormioI18n,
  FormSubmission,
  GridTemplateListItem,
} from './types';
import {
  FormioComponent,
  FormioExtendedComponentSchema,
  FormioFormBuilderConfig
} from './components/CustomBuilder/types';
import {FormioCustomNavButtons, formioNavButtons} from './EmploymentFormDetails/NavButtonSettings';
import {AllEnums} from '@/app/redux/enums';

export const normalizeString = (str: string) => {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

export const getAllComponentsFromTemplate = (
  formConfigComponents: any[],
): any[] => {
  if (!formConfigComponents || !formConfigComponents.length) {
    return [];
  }

  return formConfigComponents.reduce((acc: any, comp: any) => {
    if (!comp) {
      return acc;
    }

    if (!comp.components && !comp.columns && !comp.properties) {
      return acc;
    }

    if (comp.components) {
      return acc.concat([comp], getAllComponentsFromTemplate(comp.components));
    }

    if (comp.columns) {
      return acc.concat([comp], getAllComponentsFromTemplate(comp.columns));
    }

    return acc.concat([comp]);
  }, []);
};

export const getFormioFriendlyCurrentLanguage = getCurrentLocaleForDateUtils;

export const overrideDefaultTranslations = (
  formI18n: FormioI18n,
  currentLanguage: string,
): FormioI18n => {
  const langEntries = formI18n[currentLanguage] ?? {};
  const resolved: Record<string, string> = {};

  for (const enKey of Object.keys(langEntries)) {
    resolved[enKey] = translate.t(enKey);
  }

  return {
    ...formI18n,
    [currentLanguage]: resolved,
  };
};

export const getFormioEmbeddedTranslations = ({langCode}) => {
  try {
    return Object.assign(
        {},
        require(`@/assets/lang-formio/${langCode}.json`),
    );
  } catch (e) {
    return {};
  }
};

export const fetchAllFormTemplates = () =>
  new Promise<GridTemplateListItem[]>((resolve, reject) => {
    Service.get(
      API.forms.getAllForms(),
      (resp: GridTemplateListItem[]) => {
        resolve(resp);
      },
      (err: Error) => {
        return reject(err);
      },
    );
  });

export const fetchLatestTemplateVersion = () =>
  new Promise<string>((resolve, reject) => {
    Service.get(
      API.forms.getLatestVersion(),
      (versionData: { version: string }) => {
        resolve(versionData.version);
      },
      (err: any) => {
        reject(err);
      },
    );
  });

const fetchPendingEmployeeSummary = (empId: string | number) =>
  new Promise<EmployeeBasicData>((resolve, reject) => {
    Service.get(
      API.employee.basic(empId),
      (empData: EmployeeBasicData) => {
        resolve(empData);
      },
      (err: Error) => {
        reject(err);
      },
    );
  });

export const fetchPendingEmployees = () =>
  new Promise<EmployeeBasicData[]>((resolve, reject) => {
    Service.get(
      API.employee.pendingList(),
      (empList: EmployeeBasicData[]) => {
        resolve(empList);
      },
      (err: Error) => {
        reject(err);
      },
    );
  });

export const fetchFormSubmissions = (formId: string) =>
  new Promise<FormSubmission[]>((resolve, reject) => {
    Service.get(
      API.forms.formSubmissions(formId),
      (submissions: FormSubmission[]) => {
        resolve(submissions);
      },
      (err: Error) => {
        reject(err);
      },
    );
  });

export const fetchProvisionalEmployeeSubmissionData = (
  pendingEmpId?: string | number,
) =>
  new Promise<FormSubmission | undefined>((resolve, reject) => {
    if (!pendingEmpId) {
      return resolve(undefined);
    }

    fetchPendingEmployeeSummary(pendingEmpId).then(empData => {
      if (empData.fEmpStatus !== 'PENDING') {
        return resolve(undefined);
      }

      Service.get(
        API.forms.formProvisionalEmpSubmission(empData.fEmpPersonNumber),
        (subResp: FormSubmission) => {
          subResp.data.ssn = empData.fEmpSocSecNum;
          return resolve(subResp);
        },
        (err: Error) => {
          return reject(err);
        },
      );
    });
  });

export const fetchEmpAllowanceProposals = (empId: number, employmentId: number) =>
  new Promise<AllowanceProposalItem[]>((resolve, reject) => {
    Service.get(
      API.rewarding.allowanceProposal(empId, employmentId),
      (resp: AllowanceProposalItem[]) => {
        resolve(resp);
      },
      (err: Error) => {
        reject(err);
      },
    );
  }
);

export const fetchEmpBenefitProposals = (empId: number, employmentId: number) =>
  new Promise<BenefitProposalItem[]>((resolve, reject) => {
    Service.get(
      API.rewarding.benefitProposal(empId, employmentId),
      (resp: BenefitProposalItem[]) => {
        resolve(resp);
      },
      (err: Error) => {
        reject(err);
      },
    );
  }
);

export const fetchNewEmployeeId = (ssn: string) =>
  new Promise<number>((resolve, reject) => {
    Service.get(
      API.employee.ssn(ssn),
      (empId: number) => {
        resolve(empId);
      },
      (err: Error) => {
        reject(err);
      },
    );
  });

export const fetchDefaultFormConfig = () =>
  new Promise((resolve, reject) => {
    Service.get(
      API.forms.formDefault('employment'),
      (resp: any) => {
        resolve(resp);
      },
      (err: Error) => {
        reject(err);
      },
    );
  });

export const updateTemplateVersion = <T>(formId: string) =>
  new Promise<T>((resolve, reject) => {
    return Service.put(
      API.forms.updateVersion(formId),
      {},
      (resp: T) => {
        resolve(resp);
      },
      (err: Error) => {
        reject(err);
      },
    );
  });

export const removeNavButtonSettings = (body: FormioFormBuilderConfig, formId: string) => {
  if (!formId) {
    return;
  }
  body.components.forEach(comp => {
    if (!comp.components.length) {
      return;
    }
    const lastComponetIndex = comp.components.length - 1;
    if (
      comp.components[lastComponetIndex].key &&
      comp.components[lastComponetIndex].key.startsWith('navColumnsPage')
    ) {
      comp.components.pop();
    }
  });
};

export const addCustomNavButtonSettings = (body: FormioFormBuilderConfig) => {
  const formNumberOfPages = body.components.length;
  const updatedComponentSettings = body.components.map((comp, idx) => {
    const returnNavUpdatedComponent = (navButtons: FormioCustomNavButtons) => {
      return {
        ...comp,
        buttonSettings: {
          previous: false,
          cancel: false,
          next: false,
          submit: false,
        },
        components: [
          ...comp.components,
          navButtons,
        ],
      };
    };

    // NB: navButons are config settings used to create custom navigation buttons in formio
    if (formNumberOfPages === 1 && idx === 0) {
      const customNavButtons = formioNavButtons('1', true, false, false, true);
      return returnNavUpdatedComponent(customNavButtons);
    } else if (formNumberOfPages > 1 && idx === 0) {
      const customNavButtons = formioNavButtons('1', true, false, true, false);
      return returnNavUpdatedComponent(customNavButtons);
    } else if (formNumberOfPages > 1 && (idx + 1) < formNumberOfPages) {
      const customNavButtons = formioNavButtons(`${idx + 1}`, true, true, true, false);
      return returnNavUpdatedComponent(customNavButtons);
    } else {
      const customNavButtons = formioNavButtons(`${idx + 1}`, true, true, false, true);
      return returnNavUpdatedComponent(customNavButtons);
    }
  });

  return { ...body, components: updatedComponentSettings };
};

export const removeCustomDataPage = (config: FormioFormBuilderConfig) => {
  const index = config.components.findIndex((comp) => comp.key === 'customData');
  if (index > -1) {
    config.components.splice(index, 1);
  }
};

export const removeRewardingSectionCustomData = (config: FormioFormBuilderConfig) => {
  const rewardingSectionIndex = config.components.findIndex((comp) => comp.key === 'page6');
  if (rewardingSectionIndex === -1) {
    return;
  }
  const rewardingPage = config.components[rewardingSectionIndex];
  const index = (rewardingPage.components || []).findIndex((comp) => comp.key === 'rewardingCustomData');
  if (index !== undefined && index > -1) {
    config.components[rewardingSectionIndex].components.splice(index, 1);
  }
};

export const removeSsnFieldIfManagerRestricted = (
  config: FormioFormBuilderConfig,
) => {
  const page1Index = (config?.components || []).findIndex(
    (comp) => comp.key === 'page1',
  );
  const page1 = (config?.components || [])[page1Index];
  const ssnIndex = (page1?.components || []).findIndex(
    (comp) => comp.key === 'ssnAndRehireGroup',
  );

  if (ssnIndex > -1) {
    page1?.components?.splice(ssnIndex, 1);
    config?.components?.splice(page1Index, 1, page1);
    return config;
  }

  return config;
};

export const addCustomDataPage = (config: FormioFormBuilderConfig, components: FormioExtendedComponentSchema[]) => {
  const customDataPageExists = config.components.find((comp) => comp.key === 'customData');
  if (!customDataPageExists) {
    return;
  }
  removeCustomDataPage(config);

  const length = config.components.length;
  const componentsIndexed = components.reduce((acc, comp) => {
    return { ...acc, [comp.key]: comp };
  }, {} as Record<string, FormioExtendedComponentSchema>);

  const customDataPage = {
    title: customDataPageExists.title || 'Custom data',
    breadcrumbClickable: true,
    navigateOnEnter: false,
    saveOnEnter: false,
    scrollToTop: false,
    type: 'panel',
    key: 'customData',
    label: customDataPageExists.title || 'Custom data',
    components: customDataPageExists.components.map((comp) => {
      const disabled = componentsIndexed[comp.key]?.disabled || comp.disabled;
      const defaultValue = comp.defaultValue || componentsIndexed[comp.key]?.defaultValue;

      return {
        ...comp,
        disabled,
        defaultValue,
        validate: {
          ...comp?.validate,
          required: disabled ? false : comp?.validate?.required,
        },
      };
    }),
    input: false,
    tableView: false,
  } as unknown as FormioComponent;

  config.components.splice(length - 1, 0, customDataPage);
};

export const AllowanceProposalToFormsFormat = (allowances: AllowanceProposalItem[], allEnums: AllEnums): AllowanceProposalItemFormFormat[] => {
  return allowances.map((allowance) => ({
    fEmplSubUId: allowance.fEmplSubUId,
    allowanceAmount: allowance.fAllowanceProposalAmount,
    allowanceStartDate: allowance.fAllowanceProposalStartDate,
    allowanceEndDate: allowance.fAllowanceProposalEndDate,
    allowanceType: allEnums.ALLOWANCE_TYPE
      .map(({ groupName, code, name, description }) => ({ group: groupName, code, name, description }))
      .find((item) => item.code === allowance.fAllowanceProposalType),
    allowanceFrequency: allEnums.EVENT_FREQUENCY
      .map(({ groupName, code, name, description }) => ({ group: groupName, code, name, description }))
      .find((item) => item.code === allowance.fAllowanceProposalFrequency),
    allowanceInfo: allowance.fAllowanceProposalInfo,
    allowanceTaxableAmount: allowance.fAllowanceProposalTaxableAmount,
  }));
};

export const BenefitProposalToFormsFormat = (benefits: BenefitProposalItem[], allEnums: AllEnums): BenefitProposalItemFormFormat[] => {
  return benefits.map((benefit) => ({
    fEmplSubUId: benefit.fEmplSubUId,
    benefitAmount: benefit.fBenefitProposalAmount,
    benefitStartDate: benefit.fBenefitProposalStartDate,
    benefitEndDate: benefit.fBenefitProposalEndDate,
    benefitType: allEnums.BENEFIT_TYPE
      .map(({ groupName, code, name, description }) => ({ group: groupName, code, name, description }))
      .find((item) => item.code === benefit.fBenefitProposalType),
    benefitFrequency: allEnums.EVENT_FREQUENCY
      .map(({ groupName, code, name, description }) => ({ group: groupName, code, name, description }))
      .find((item) => item.code === benefit.fBenefitProposalFrequency),
    benefitInfo: benefit.fBenefitProposalInfo,
    benefitTaxableAmount: benefit.fBenefitProposalTaxableAmount,
  }));
};

type AccessState = 'hidden' | 'readonly' | 'editable';

const hasRole = (userRoles: string[], allowed?: string[]) => {
  if (!allowed || allowed.length === 0) {
    return true;
  }
  return allowed.some(role => userRoles.includes(role));
};

const resolveAccessState = (
    component: any,
    userRoles: string[],
): AccessState => {
  const access = component.access;

  // default: backward compatible
  if (!access) {
    return 'editable';
  }

  if (access.read && !hasRole(userRoles, access.read)) {
    return 'hidden';
  }

  if (access.write && !hasRole(userRoles, access.write)) {
    return 'readonly';
  }

  return 'editable';
};

export const applyAccessControl = (
    components: any[],
    userRoles: string[],
): any[] => {
  if (!Array.isArray(components)) {
    return components;
  }

  return components
      .map(component => {
        const state = resolveAccessState(component, userRoles);
        if (state === 'hidden') {
          return null;
        }

        const next = {...component};

        if (state === 'readonly') {
          next.disabled = true;
        }

        if (Array.isArray(next.components)) {
          next.components = applyAccessControl(next.components, userRoles);
        }

        return next;
      })
      .filter(Boolean);
};